// Eigene Plugins (Bridge) in Spuren und Bussen: welcher Teil der Insert-Kette schon gerechnet ist.
//
// Eine Spur mit eigenen Plugins klingt so:
//   Eingang (Clips/Instrument) → Inserts bis einschließlich des letzten eigenen Plugins (upTo)
//     → [von der Bridge gerechnet: offline als Datei (bridgeRender) oder live in Stücken]
//   → restliche Inserts im Browser → Panner, Fader, Sends.
// Solange nichts Gültiges vorliegt, laufen die eigenen Plugins als Durchreiche (trocken).
// Reine Funktionen ohne Abhängigkeit von Engine oder Bridge (werden von MixGraph und Engine benutzt).
import type { InsertSlot, Project, Track } from '@srs/shared';
import { freezeSig } from '../edit/mixops.ts';

export const isBridgeSlot = (s: InsertSlot) => s.plugin.startsWith('bridge:');

/** erste und letzte Stelle eines eigenen Plugins in der Kette (nicht im Bypass mitgezählt: Bypass bleibt in der Kette) */
export function bridgeRange(t: Track): { first: number; last: number } | null {
  let first = -1, last = -1;
  t.inserts.forEach((s, i) => { if (isBridgeSlot(s)) { if (first < 0) first = i; last = i; } });
  return first < 0 ? null : { first, last };
}

export const hasBridge = (t: Track) => t.inserts.some(isBridgeSlot);

const fnv = (s: string, seed = 2166136261) => { let h = seed; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return (h >>> 0).toString(36); };
/** längerer Streuwert (zwei FNV-Läufe), als Schlüssel für die Bridge (nur [a-z0-9]) */
export const hash2 = (s: string) => fnv(s) + fnv(s, 0x811c9dc5 ^ 0x5bd1e995);

const isBus = (p: Project, t: Track) => (p.busses ?? []).some((b) => b.id === t.id) || t.kind === 'bus';

const slotSig = (list: InsertSlot[]) => list.map((s) => ({ plugin: s.plugin, params: s.params, state: s.state, bypass: s.bypass }));

/** Eingang der eigenen Plugins: alles, was vor dem ersten eigenen Plugin klingt */
export function drySig(p: Project, t: Track): string {
  const r = bridgeRange(t);
  const before = r ? t.inserts.slice(0, r.first) : t.inserts;
  const ids = new Set(before.map((s) => s.id));
  const auto = Object.fromEntries(Object.entries(t.automation?.lanes ?? {}).filter(([k]) => k.startsWith('ins:') && ids.has(k.split(':')[1])));
  if (isBus(p, t)) {
    // Bus: Summe der Sends und der darauf gerouteten Spuren (samt deren gerechneter eigener Plugins)
    const feed = p.tracks.filter((x) => x.kind !== 'bus' && (x.output === t.id || x.sends.some((s) => s.busId === t.id && s.level > 0)))
      .map((x) => ({
        f: freezeSig(p, x), v: x.volumeDb, m: x.mute, s: x.solo, pan: x.pan, sends: x.sends.filter((s) => s.busId === t.id), out: x.output,
        auto: Object.fromEntries(Object.entries(x.automation?.lanes ?? {}).filter(([k]) => k === 'volume' || k === 'pan' || k === 'mute' || k === `send:${t.id}`)),
        fx: x.bridgeRender && rendered(p, x) ? x.bridgeRender.sig : '',
      }));
    return hash2(JSON.stringify({ bus: t.id, feed, ins: slotSig(before), auto, solo: p.tracks.some((x) => x.solo) }));
  }
  const clips = p.clips.filter((c) => c.trackId === t.id).map((c) => ({ ...c, id: undefined, by: undefined, name: undefined, groupId: undefined }));
  return hash2(JSON.stringify({ clips, ins: slotSig(before), auto, inst: t.instrument, el: t.elastic, tempo: [p.bpm, p.tempos, p.meters, p.beatsPerBar] }));
}

/** Stand der gerechneten Fassung: Eingang plus alle Inserts bis einschließlich des letzten eigenen Plugins */
export function fxSig(p: Project, t: Track): string {
  const r = bridgeRange(t);
  if (!r) return '';
  return hash2(drySig(p, t) + JSON.stringify(slotSig(t.inserts.slice(r.first, r.last + 1))));
}

/** Verbindung zur Engine: vorhandene Audiodaten und live laufende Ketten (setzt die Engine bzw. bridgefx) */
export const fx = {
  has: (_sourceId: string) => false,
  /** Spuren/Busse, deren eigene Plugins gerade live von der Bridge kommen */
  live: new Set<string>(),
};

/** Gültige gerechnete Fassung vorhanden? */
export const rendered = (p: Project, t: Track) => !!t.bridgeRender && hasBridge(t) && t.bridgeRender.sig === fxSig(p, t) && fx.has(t.bridgeRender.sourceId);

/** Kommt der Klang der eigenen Plugins gerade von der Bridge (live oder gerechnet)? */
export const fxActive = (p: Project, t: Track, allowLive: boolean) => hasBridge(t) && ((allowLive && fx.live.has(t.id)) || rendered(p, t));

/** Inserts, die der Browser selbst rechnet */
export function browserInserts(p: Project, t: Track, allowLive: boolean): InsertSlot[] {
  const r = bridgeRange(t);
  if (!r || !fxActive(p, t, allowLive)) return t.inserts;
  return t.inserts.slice(r.last + 1);
}
